import { lazy, type ComponentType } from 'react';
import { logger } from '@core/utils/logger';

/** Survives the reload it guards, and nothing beyond that tab. */
const RECOVERED_KEY = 'dda.chunk-reload';

type Loader<T extends ComponentType<unknown>> = () => Promise<{ default: T }>;

const NEVER = new Promise<never>(() => undefined);

/**
 * A route chunk that survives a deploy landing under an open tab.
 *
 * `s3 sync --delete` removes the previous content hashes, and the edge answers a
 * missing `/assets/*` with the SPA shell at HTTP 200 rather than a 404 — so the import
 * fails on being handed HTML, and the route dies on what the reader experienced as an
 * ordinary click. Retrying returns the same shell; only fetching the new `index.html`
 * helps, which is a reload. Once, because a reload loop is worse than an error page:
 * if it still will not load afterwards the cause is not a stale bundle.
 */
export function lazyRoute<T extends ComponentType<unknown>>(load: Loader<T>): React.LazyExoticComponent<T> {
  return lazy(() =>
    load().then(
      (module) => {
        sessionStorage.removeItem(RECOVERED_KEY);
        return module;
      },
      (error: unknown) => {
        if (sessionStorage.getItem(RECOVERED_KEY) !== null) {
          logger.error('Route chunk failed again after reloading', { message: String(error) });
          throw error;
        }
        sessionStorage.setItem(RECOVERED_KEY, '1');
        logger.warn('Route chunk missing, reloading for the current build', { message: String(error) });
        window.location.reload();
        // Never settles: resolving would render a route against a bundle we have just
        // decided not to trust, and the reload is already replacing this document.
        return NEVER as Promise<{ default: T }>;
      },
    ),
  );
}
